import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Type_Conversion() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔄 JavaScript Type Conversion</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript is a <b>dynamically typed language</b>, meaning variables can
        change their type during execution. Type conversion refers to changing a
        value from one data type to another — either <b>implicitly</b> (by
        JavaScript automatically) or <b>explicitly</b> (by the developer).
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>Implicit Conversion (Type Coercion):</b> JavaScript automatically
            converts types during operations.
          </li>
          <li>
            <b>Explicit Conversion:</b> You manually convert using functions
            like <code className="highlight_code">String()</code>,{" "}
            <code className="highlight_code">Number()</code>, or{" "}
            <code className="highlight_code">Boolean()</code>.
          </li>
          <li>
            Be careful with <b>unexpected results</b> during implicit coercion.
          </li>
        </ul>
      </div>

      {/* Section 1: Implicit Type Conversion */}
      <div className="tutorial-section">
        <h2 className="section-title">
          1️⃣ Implicit Type Conversion (Type Coercion)
        </h2>
        <p>
          JavaScript automatically converts types when performing operations
          between different data types.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log("5" + 2);   // "52"  (number → string)
console.log("5" - 2);   // 3    (string → number)
console.log("5" * "2"); // 10   (both → number)
console.log("5" + true); // "5true"`}
          />
          <TryItButton
            code={`console.log("5" + 2);
console.log("5" - 2);
console.log("5" * "2");
console.log("5" + true);`}
          />
        </div>

        <OutputBox>
          <pre>
            52{"\n"}3{"\n"}10{"\n"}5true
          </pre>
        </OutputBox>
      </div>

      {/* Section 2: Explicit Type Conversion */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Explicit Type Conversion</h2>
        <p>
          You can manually convert data types using global functions like{" "}
          <code className="highlight_code">String()</code>,{" "}
          <code className="highlight_code">Number()</code>, and{" "}
          <code className="highlight_code">Boolean()</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let num = 25;
console.log(String(num));  // "25"
console.log(Number("25")); // 25
console.log(Boolean(0));   // false`}
          />
          <TryItButton
            code={`let num = 25;
console.log(String(num));
console.log(Number("25"));
console.log(Boolean(0));`}
          />
        </div>

        <OutputBox>
          <pre>
            25{"\n"}25{"\n"}false
          </pre>
        </OutputBox>
      </div>

      {/* Section 3: Conversion to String */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Conversion to String</h2>
        <p>
          Use <code className="highlight_code">String()</code> or{" "}
          <code className="highlight_code">toString()</code> to convert values
          to strings.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(String(123));    // "123"
console.log((123).toString()); // "123"
console.log(String(true));     // "true"`}
          />
          <TryItButton
            code={`console.log(String(123));
console.log((123).toString());
console.log(String(true));`}
          />
        </div>

        <OutputBox>
          <pre>
            123{"\n"}123{"\n"}true
          </pre>
        </OutputBox>
      </div>

      {/* Section 4: Conversion to Number */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Conversion to Number</h2>
        <p>
          Use <code className="highlight_code">Number()</code> or mathematical
          operations to convert values to numbers.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(Number("123"));  // 123
console.log(Number("abc"));  // NaN
console.log(parseInt("10px")); // 10
console.log(+ "5"); // 5 (unary plus)`}
          />
          <TryItButton
            code={`console.log(Number("123"));
console.log(Number("abc"));
console.log(parseInt("10px"));
console.log(+ "5");`}
          />
        </div>

        <OutputBox>
          <pre>
            123{"\n"}NaN{"\n"}10{"\n"}5
          </pre>
        </OutputBox>
      </div>

      {/* Section 5: Conversion to Boolean */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Conversion to Boolean</h2>
        <p>
          Falsy values include: <code className="highlight_code">0</code>,{" "}
          <code className="highlight_code">""</code>,{" "}
          <code className="highlight_code">null</code>,{" "}
          <code className="highlight_code">undefined</code>, and{" "}
          <code className="highlight_code">NaN</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(Boolean(0));       // false
console.log(Boolean(""));      // false
console.log(Boolean("text"));  // true
console.log(Boolean([]));      // true (empty array is truthy)`}
          />
          <TryItButton
            code={`console.log(Boolean(0));
console.log(Boolean(""));
console.log(Boolean("text"));
console.log(Boolean([]));`}
          />
        </div>

        <OutputBox>
          <pre>
            false{"\n"}false{"\n"}true{"\n"}true
          </pre>
        </OutputBox>
      </div>

      {/* Section 6: Complex Example */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Complex Example: Mixed Conversions</h2>
        <p>
          JavaScript performs conversions automatically in expressions involving
          multiple data types.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(5 + "5");    // "55"
console.log("5" * 2);   // 10
console.log("5" - true); // 4
console.log("5" + true); // "5true"`}
          />
          <TryItButton
            code={`console.log(5 + "5");
console.log("5" * 2);
console.log("5" - true);
console.log("5" + true);`}
          />
        </div>

        <OutputBox>
          <pre>
            55{"\n"}10{"\n"}4{"\n"}5true
          </pre>
        </OutputBox>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>Use explicit conversion to avoid unexpected coercion results.</li>
          <li>
            Always handle <code className="highlight_code">NaN</code> when
            converting strings to numbers.
          </li>
          <li>
            Check types with <code className="highlight_code">typeof</code>{" "}
            before conversion if unsure.
          </li>
          <li>
            Remember that <code className="highlight_code">[]</code> and{" "}
            <code className="highlight_code">{}</code> are truthy values.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn more about <b>Typeof Operator</b> to check variable types.
          </li>
          <li>
            Explore <b>toString()</b> and <b>valueOf()</b> methods.
          </li>
          <li>
            Understand <b>Comparison Operators</b> that use type conversion
            under the hood.
          </li>
        </ul>
      </div>
    </div>
  );
}
